import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  CircularProgress, 
  Divider, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper, 
  Step, 
  StepContent, 
  StepLabel, 
  Stepper, 
  Typography 
} from '@mui/material';
import { 
  CheckCircleOutline, 
  ErrorOutline, 
  Info, 
  Link as LinkIcon, 
  Security, 
  Settings, 
  VpnKey 
} from '@mui/icons-material';
import axios from 'axios';
import { auth } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

const CoinbaseSetupGuide: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [serverStatus, setServerStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [userId, setUserId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check server status
    const checkServer = async () => {
      try {
        await axios.head('http://localhost:3001/api/coinbase');
        setServerStatus('online');
      } catch (error) {
        setServerStatus('offline');
      }
    };

    // Get current user ID
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });

    checkServer();
    
    // Check server status every 10 seconds
    const intervalId = setInterval(checkServer, 10000);

    return () => {
      unsubscribe();
      clearInterval(intervalId);
    };
  }, []);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const steps = [
    {
      label: 'Prerequisites',
      description: (
        <Box>
          <Typography variant="body1" gutterBottom>
            Before setting up Coinbase integration, ensure you have:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutline color={serverStatus === 'online' ? 'success' : 'error'} />
              </ListItemIcon>
              <ListItemText 
                primary="Server Status" 
                secondary={
                  serverStatus === 'checking' ? 'Checking...' : 
                  serverStatus === 'online' ? 'Online' : 'Offline - Please start the server'
                } 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleOutline color={userId ? 'success' : 'error'} />
              </ListItemIcon>
              <ListItemText 
                primary="User Authentication" 
                secondary={userId ? `Logged in as ${userId}` : 'Not logged in'} 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LinkIcon />
              </ListItemIcon>
              <ListItemText 
                primary="Coinbase Account" 
                secondary="You need an active Coinbase account" 
              />
            </ListItem>
          </List>
          {serverStatus === 'offline' && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff9c4', mt: 2 }}>
              <Typography variant="body2" color="error">
                <ErrorOutline sx={{ verticalAlign: 'middle', mr: 1 }} />
                The server is offline. Please start the server by running:
              </Typography>
              <Box component="pre" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, mt: 1 }}>
                cd server
                npm run dev
              </Box>
            </Paper>
          )}
          {!userId && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: '#fff9c4', mt: 2 }}>
              <Typography variant="body2" color="error">
                <ErrorOutline sx={{ verticalAlign: 'middle', mr: 1 }} />
                You need to be logged in to continue.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 1 }}
                onClick={() => navigate('/sign-in')}
              >
                Go to Login
              </Button>
            </Paper>
          )}
        </Box>
      ),
    },
    {
      label: 'Create Coinbase API Keys',
      description: (
        <Box>
          <Typography variant="body1" gutterBottom>
            Follow these steps to create your Coinbase API keys:
          </Typography>
          <List sx={{ pl: 2 }} component="ol">
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText primary="Log in to your Coinbase account" />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText primary="Navigate to Settings > API" />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText primary="Click 'New API Key'" />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText 
                primary="Set the following permissions:" 
                secondary="wallet:accounts:read (required), wallet:buys:create, wallet:sells:create (optional)"
              />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText primary="Complete the security verification" />
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText 
                primary="Save your API Key and API Secret" 
                secondary="IMPORTANT: The API Secret will only be shown once. Make sure to copy it immediately."
              />
            </ListItem>
          </List>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', mt: 2 }}>
            <Typography variant="body2" color="primary">
              <Info sx={{ verticalAlign: 'middle', mr: 1 }} />
              For security reasons, we recommend creating API keys with minimal permissions required for portfolio tracking.
            </Typography>
          </Paper>
        </Box>
      ),
    },
    {
      label: 'Store API Keys Securely',
      description: (
        <Box>
          <Typography variant="body1" gutterBottom>
            Your API keys will be stored securely in our database with encryption. You have two options:
          </Typography>
          
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Security sx={{ verticalAlign: 'middle', mr: 1 }} />
                Option 1: Use the Server Script (Recommended)
              </Typography>
              <Typography variant="body2" paragraph>
                This is the most secure method as your keys are encrypted before storage.
              </Typography>
              <Typography variant="body2" component="div">
                Run the following command in your terminal:
                <Box component="pre" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, mt: 1 }}>
                  cd server
                  node scripts/setup-coinbase-credentials.js {userId} YOUR_API_KEY YOUR_API_SECRET
                </Box>
              </Typography>
            </CardContent>
          </Card>
          
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <VpnKey sx={{ verticalAlign: 'middle', mr: 1 }} />
                Option 2: Enter Keys in the App (Legacy)
              </Typography>
              <Typography variant="body2" paragraph>
                This method is less secure but easier for testing.
              </Typography>
              <Typography variant="body2">
                You can enter your API keys directly in the Coinbase configuration section of the app.
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => navigate('/coinbase-config')}
              >
                <Settings sx={{ mr: 1 }} />
                Go to Coinbase Config
              </Button>
            </CardContent>
          </Card>
          
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#e3f2fd', mt: 2 }}>
            <Typography variant="body2">
              <Info sx={{ verticalAlign: 'middle', mr: 1 }} />
              Your API keys are used to access your Coinbase account data. We never store your keys in plain text.
            </Typography>
          </Paper>
        </Box>
      ),
    },
    {
      label: 'Verify Integration',
      description: (
        <Box>
          <Typography variant="body1" gutterBottom>
            Let's verify that your Coinbase integration is working correctly:
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <VpnKey />
              </ListItemIcon>
              <ListItemText 
                primary="Test API Connection" 
                secondary="Run the test script to verify your API keys are working"
              />
              <Box component="pre" sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, ml: 2, flexGrow: 1 }}>
                cd server
                node scripts/test-coinbase-integration.js {userId}
              </Box>
            </ListItem>
            <Divider sx={{ my: 2 }} />
            <ListItem>
              <ListItemIcon>
                <Settings />
              </ListItemIcon>
              <ListItemText 
                primary="Check Portfolio Page" 
                secondary="Navigate to the Portfolio page to see your Coinbase data"
              />
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => navigate('/portfolio')}
              >
                Go to Portfolio
              </Button>
            </ListItem>
          </List>
          
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#e8f5e9', mt: 2 }}>
            <Typography variant="body2">
              <CheckCircleOutline sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }} />
              If everything is set up correctly, you should see your Coinbase portfolio data on the Portfolio page.
            </Typography>
          </Paper>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Coinbase Integration Setup Guide
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        Follow these steps to securely connect your Coinbase account to Strategiz
      </Typography>
      
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                {step.description}
                <Box sx={{ mb: 2, mt: 2 }}>
                  <div>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mt: 1, mr: 1 }}
                      disabled={
                        (index === 0 && (serverStatus !== 'online' || !userId))
                      }
                    >
                      {index === steps.length - 1 ? 'Finish' : 'Continue'}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Back
                    </Button>
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
        {activeStep === steps.length && (
          <Paper square elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <CheckCircleOutline sx={{ verticalAlign: 'middle', mr: 1, color: 'success.main' }} />
              Setup Complete!
            </Typography>
            <Typography paragraph>
              You have successfully set up the Coinbase integration. Your portfolio data should now be available in the app.
            </Typography>
            <Button onClick={handleReset} sx={{ mt: 1, mr: 1 }}>
              Reset Guide
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => navigate('/portfolio')}
              sx={{ mt: 1, mr: 1 }}
            >
              Go to Portfolio
            </Button>
          </Paper>
        )}
      </Paper>
      
      <Paper elevation={1} sx={{ p: 2, bgcolor: '#fff8e1' }}>
        <Typography variant="subtitle2" gutterBottom>
          <Info sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
          Security Notice
        </Typography>
        <Typography variant="body2">
          Your Coinbase API credentials are sensitive information. We store them securely with encryption, and all API requests are made server-side.
          Never share your API keys or enter them on untrusted websites.
        </Typography>
      </Paper>
    </Box>
  );
};

export default CoinbaseSetupGuide;
